import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { SaveAltIcon, Spinner } from 'tuui';
import {
    HeadlessDropdown,
    INDICATION_TYPES,
    STYLED_BUTTON_SIZE,
    STYLED_BUTTON_TYPE,
    StyledButton,
    TOOLTIP_POSITION,
    TooltipV2 as Tooltip,
} from 'taboola-ultimate-ui';
import { ExternalLink } from 'components';
import { useAvailableItems } from 'hooks';
import { useReportExportAllowed } from 'modules/campaigns/modules/campaigns-reports/components/GridToolbar/hooks/useReportExportAllowed';
import numberFormatter from 'modules/campaigns/modules/campaigns-reports/components/ValueFormatters/numberFormatter';
import { addIndication } from 'modules/taboola-common-frontend-modules/Indications/actions';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import {
    exportToGoogleDriveCSVFile,
    exportToLocalCSVFile,
    asyncExportToLocalCSVFile,
} from 'modules/taboola-common-frontend-modules/csvExporter/csvExporter';
import { useGoogleApi } from 'modules/taboola-common-frontend-modules/googleApi';
import { GTM_EVENTS, gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { getPageName } from 'modules/taboola-common-frontend-modules/gtmTracker/urlPageNameUtil';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { useWebSocket } from 'modules/taboola-common-frontend-modules/web-socket-provider/hooks/useWebSocket';
import { isDynamicReport } from '../../../../../config/isStaticDynamicReport';
import { DROPDOWN_OPTIONS, ERROR_INDICATIONS, EXPORT_TYPES } from './constants';
import styles from './exportDropdown.module.scss';

const ExportDropdown = ({
    csvOptionsProvider,
    label,
    disableGoogleDriveExport,
    reportConfig,
    totalRows,
    exportButtonId,
}) => {
    const {
        isVisible: showExport,
        isAllowed: isAllowedExport,
        maxAllowedRows,
        isAsync: isAsyncExport,
    } = useReportExportAllowed({ reportConfig, totalRows });
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isGoogleDriveExportLoading, setIsGoogleDriveExportLoading] = useState(false);
    const [link, setLink] = useState(null);
    const dispatch = useDispatch();
    const intl = useIntl();
    const { checkAuth, loadGoogleSheets } = useGoogleApi();

    const isGoogleDriveExportConfigured = useConfigMatch({
        [FEATURE_FLAGS.GOOGLE_DRIVE_EXPORT_ENABLED]: 'true',
    });
    const isReportDynamic = isDynamicReport(reportConfig.id);
    const isGoogleDriveExportEnabled = isGoogleDriveExportConfigured && !disableGoogleDriveExport;
    const onToggleDropdownHandler = useCallback(() => setIsOpen(prev => !prev), []);
    const options = useAvailableItems(DROPDOWN_OPTIONS, { isGoogleDriveExportEnabled });

    useWebSocket(data => {
        if (!data) {
            return false;
        }

        if (data?.eventDescription === 'REPORT_URL') {
            const url = data?.additionalData[0]?.additionalDataEntries[0]?.value;
            setLink(url);
            if (!isGoogleDriveExportLoading) {
                handleAsyncExport(url);
            } else {
                handleGoogleDriveExportDispatch(url);
            }
        }
    });

    const handleLocalCSVExport = useCallback(async () => {
        try {
            const { columnDefs, selectedColumns, filename, dataProvider, injectables } =
                csvOptionsProvider(isAsyncExport);

            setLoading(true);
            await exportToLocalCSVFile(dataProvider, columnDefs, selectedColumns, filename, injectables, isAsyncExport);
            if (!isAsyncExport) {
                setLoading(false);
            }
            if (!isGoogleDriveExportEnabled) {
                gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
                    pageName: getPageName(),
                    component: 'ExportToCsvButton',
                });
            }
        } catch (err) {
            console.error(err);
            dispatch(addIndication(ERROR_INDICATIONS.CSV));
        }
    }, [csvOptionsProvider, isAsyncExport, isGoogleDriveExportEnabled, dispatch]);

    const handleAsyncExport = useCallback(
        async link => {
            const { filename } = csvOptionsProvider(isAsyncExport);
            await asyncExportToLocalCSVFile({ link, filename });
            setLoading(false);
        },
        [csvOptionsProvider, isAsyncExport]
    );

    const handleAsyncExportGoogleDrive = useCallback(async () => {
        const { dataProvider } = await csvOptionsProvider(isAsyncExport);
        await dataProvider();
    }, [csvOptionsProvider, isAsyncExport]);

    const handleGoogleDriveExportDispatch = useCallback(
        async link => {
            const { columnDefs, selectedColumns, filename, dataProvider, injectables } = await csvOptionsProvider(
                isAsyncExport
            );
            const spreadsheetUrl = await exportToGoogleDriveCSVFile(
                dataProvider,
                columnDefs,
                selectedColumns,
                filename,
                injectables,
                isAsyncExport,
                link
            );
            setLoading(false);
            setIsGoogleDriveExportLoading(false);
            dispatch(addIndication(getGoogleDriveSuccessIndication(spreadsheetUrl)));
        },
        [isAsyncExport, csvOptionsProvider, dispatch]
    );

    const handleGoogleDriveExport = useCallback(async () => {
        let spreadsheetUrl = '';

        try {
            // log user into google drive
            await checkAuth();

            // // load google sheets API
            await loadGoogleSheets();

            const { columnDefs, selectedColumns, filename, dataProvider, injectables } = await csvOptionsProvider(
                isAsyncExport
            );

            if (isAsyncExport) {
                setIsGoogleDriveExportLoading(true);
                handleAsyncExportGoogleDrive();
            } else {
                spreadsheetUrl = await exportToGoogleDriveCSVFile(
                    dataProvider,
                    columnDefs,
                    selectedColumns,
                    filename,
                    injectables,
                    isAsyncExport,
                    link
                );

                setLoading(false);
                // create success indication with spreadsheet link
                dispatch(addIndication(getGoogleDriveSuccessIndication(spreadsheetUrl)));
            }
        } catch (err) {
            console.error(err);
            dispatch(addIndication(ERROR_INDICATIONS.GOOGLE_DRIVE));
        }
    }, [csvOptionsProvider, isAsyncExport, handleAsyncExportGoogleDrive, link, dispatch, checkAuth, loadGoogleSheets]);

    const onChangeHandler = useCallback(
        async ({ value }) => {
            setLoading(true);

            gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
                pageName: getPageName(),
                component: 'ExportToCsvButton',
                value,
            });

            const exportFuncByType = {
                [EXPORT_TYPES.GOOGLE_DRIVE]: handleGoogleDriveExport,
                [EXPORT_TYPES.CSV]: handleLocalCSVExport,
            };

            await exportFuncByType[value]();
        },
        [handleGoogleDriveExport, handleLocalCSVExport]
    );

    const tooltipMessageId = isAllowedExport
        ? 'app.gridToolbar.export.tooltip'
        : isReportDynamic
        ? 'app.dynamicReports.gridToolbar.export.limited.tooltip'
        : 'app.gridToolbar.export.disabled.tooltip';

    if (!showExport) {
        return null;
    }

    return (
        <HeadlessDropdown
            options={options}
            onChange={onChangeHandler}
            open={isOpen}
            onToggleDropdown={isGoogleDriveExportEnabled ? onToggleDropdownHandler : handleLocalCSVExport}
            disabled={!isAllowedExport || loading}
            width={175}
            menuStyles={{ textAlign: 'left' }}
            isRtl
        >
            <Tooltip arrow={true} position={TOOLTIP_POSITION.BOTTOM_START}>
                {isReportDynamic && !isAllowedExport ? (
                    <FormattedMessage
                        id={tooltipMessageId}
                        defaultMessage="Export"
                        values={{
                            maxRows: numberFormatter({
                                value: maxAllowedRows,
                                i18n: intl,
                            }),
                        }}
                    />
                ) : (
                    <FormattedMessage id={tooltipMessageId} defaultMessage="Export" />
                )}
            </Tooltip>

            <StyledButton
                id={exportButtonId}
                className={styles['export-button']}
                size={STYLED_BUTTON_SIZE.MEDIUM}
                type={STYLED_BUTTON_TYPE.GHOST}
                disabled={loading}
                aria-label="ExportButton"
            >
                {loading ? (
                    <Spinner size={14} />
                ) : (
                    <>
                        <SaveAltIcon /> {label}
                    </>
                )}
            </StyledButton>
        </HeadlessDropdown>
    );
};

const ONE_MINUTE = 60 * 1000;

const getGoogleDriveSuccessIndication = url => ({
    message: (
        <FormattedMessage
            id="app.campaigns.reports.csv.googleDrive.success"
            defaultMessage="Report exported to Google Drive successfully. {link}"
            values={{
                link: (
                    <ExternalLink href={url}>
                        <FormattedMessage
                            id="app.campaigns.reports.csv.googleDrive.success.link"
                            defaultMessage="Open on Google Drive"
                        />
                    </ExternalLink>
                ),
            }}
        />
    ),
    type: INDICATION_TYPES.SUCCESS,
    highlight: <FormattedMessage id="success.highlight" defaultMessage="Success!" />,
    timeoutDuration: ONE_MINUTE * 2,
});

ExportDropdown.propTypes = {
    csvOptionsProvider: PropTypes.func,
    isAllowedExport: PropTypes.bool,
};

export default ExportDropdown;
